import { ObjectId } from 'mongodb';
import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageSquare, MessageCircle, Calendar, Eye, Flag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { PopulatedDatabaseQuestion, SafeDatabaseUser } from '../../../../types/types';
import { TagsDisplay } from '../../../form';

import { Button } from '../../../ui/button';
import UserContext from '../../../../contexts/UserContext';
import ReportPostModal from '../../reportPostModal';

/**
 * Interface representing the props for the Question component.
 *
 * q - The question object containing details about the question.
 */
interface QuestionProps {
  question: PopulatedDatabaseQuestion;
}

/**
 * Question component renders the details of a question including its title, tags, author, answers, and views.
 * Clicking on the component triggers the handleAnswer function,
 * and clicking on a tag triggers the clickTag function.
 *
 * @param q - The question object containing question details.
 */
const QuestionView = ({ question }: QuestionProps) => {
  const navigate = useNavigate();
  const context = useContext(UserContext);
  const currentUser = context ? context.user : null;
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  /**
   * Function to navigate to the home page with the specified tag as a search parameter.
   *
   * @param tagName - The name of the tag to be added to the search parameters.
   */
  const clickTag = (tagName: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tag', tagName);

    navigate(`/home?${searchParams.toString()}`);
  };

  /**
   * Function to navigate to the specified question page based on the question ID.
   *
   * @param questionID - The ID of the question to navigate to.
   */
  const handleAnswer = (questionID: ObjectId) => {
    navigate(`/question/${questionID}`);
  };
  /**
   * Function to handle reporting a post
   */
  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReportModalOpen(true);
  };

  // Don't show report button if viewing own post or not logged in
  const showReportButton = currentUser && currentUser.username !== question.askedBy;

  // Format date to show relative time
  const formatRelativeTime = (dateValue: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateValue.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
  };

  return (
    <>
      <div className='p-4'>
        <div className='flex items-start gap-3'>
          <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary'>
            <MessageSquare size={18} />
          </div>

          <div className='flex-1'>
            <div className='flex flex-wrap justify-between gap-2 mb-1'>
              <Link
                to={`/user/${question.askedBy}`}
                className='font-medium text-blue-600 hover:underline'>
                {question.askedBy}
              </Link>
              <div className='flex flex-wrap justify-end gap-2'>
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Calendar size={14} className='mr-1' />
                  {formatRelativeTime(new Date(question.askDateTime))}
                </div>
                {showReportButton && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='report-button'
                    onClick={handleReportClick}
                    title='Report post'>
                    <Flag className='h-4 w-4 text-red-500' />
                  </Button>
                )}
              </div>
            </div>

            <div className='mb-2'>
              <span className='text-sm text-muted-foreground'>asked a question</span>
            </div>

            <div
              className='cursor-pointer'
              onClick={() => {
                if (question._id) {
                  handleAnswer(question._id);
                }
              }}>
              <h3 className='text-lg font-medium hover:text-primary transition-colors'>
                {question.title}
              </h3>

              <div className='mt-2 text-muted-foreground line-clamp-2 prose prose-sm max-w-none'>
                <ReactMarkdown>{question.text}</ReactMarkdown>
              </div>

              <TagsDisplay tags={question.tags} onTagClick={clickTag} className='mt-3' />
            </div>

            <div className='mt-3 flex items-center text-sm text-muted-foreground'>
              <div className='flex items-center mr-4'>
                <MessageCircle size={14} className='mr-1' />
                {question.answers.length} answer{question.answers.length !== 1 ? 's' : ''}
              </div>
              <div className='flex items-center'>
                <Eye size={14} className='mr-1' />
                {question.views.length} view{question.views.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showReportButton && (
        <ReportPostModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          post={question}
          currentUser={currentUser as SafeDatabaseUser}
        />
      )}
    </>
  );
};

export default QuestionView;
